import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Alert, Button, Paper, Tabs, Tab, TextField, MenuItem, Chip, LinearProgress, Tooltip, IconButton } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useNavigate, useParams } from 'react-router-dom';
import * as Icons from '../constants/icons';
import LoadingView from '../components/LoadingView';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import MailPreviewFrame from '../components/mailDelivery/MailPreviewFrame';
import { CampaignStatusChip, DeliveryStatusChip } from '../components/mailDelivery/MailStatusChip';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useAuth } from '../firebase/AuthProvider';
import {
  useMailCampaign,
  useMailDeliveries,
  mailApi,
  MailDeliveryRow,
  MailDeliveryStatus,
  DELIVERY_STATUS_LABEL,
  fmtDateTime,
  fmtCountRate,
} from '../hooks/useMailDelivery';

/**
 * メール配信 配信詳細 (/mail-delivery/campaigns/:id)。
 *
 * 上部にサマリーカード (配信数 / 送信 / 配信成功 / バウンス / 苦情 / 失敗 / 除外 / 開封 / クリック)、
 * 下部タブで 宛先一覧 (状態・検索つき、顧客詳細へ遷移) と 本文プレビュー。
 * 送信中はフック側が 10 秒間隔で再取得するので進捗バーが動く。
 */
const MailDeliveryCampaignDetail: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? Number(idParam) : null;
  const navigate = useNavigate();
  const notify = useNotification();
  const confirm = useConfirm();
  const { user } = useAuth();
  const operator = user?.email || 'unknown';

  const { campaign, loading, error, reload } = useMailCampaign(id);
  const [tab, setTab] = useState<'recipients' | 'preview'>('recipients');
  const deliveries = useMailDeliveries(id, tab === 'recipients');
  const [qInput, setQInput] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  // 本文プレビュー (差し込み見本つき) はタブを開いたときに生成する
  useEffect(() => {
    if (tab !== 'preview' || !campaign || previewHtml) return;
    const blocks = campaign.blocks_json || [];
    if (blocks.length === 0) {
      setPreviewHtml(campaign.body_html);
      return;
    }
    mailApi.preview(blocks, campaign.body_text).then((r) => setPreviewHtml(r.ok ? r.data.html : campaign.body_html));
  }, [tab, campaign, previewHtml]);

  // 送信中の進捗が更新されたら宛先一覧も追従させる
  useEffect(() => {
    if (campaign?.status === 'sending' && tab === 'recipients') deliveries.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.sent_count, campaign?.delivered_count]);

  const handleCopy = async () => {
    if (!campaign) return;
    const r = await mailApi.copyCampaign(campaign.id, operator);
    if (!r.ok) return notify.error(r.error);
    notify.success('複製して下書きを作成しました');
    navigate(`/mail-delivery/campaigns/${r.data.id}/edit`);
  };

  const handleCancel = async () => {
    if (!campaign) return;
    const ok = await confirm({ title: '予約の取消', message: `「${campaign.subject}」の配信予約を取り消しますか？`, confirmText: '取り消す', destructive: true });
    if (!ok) return;
    const r = await mailApi.cancelCampaign(campaign.id, operator);
    if (!r.ok) return notify.error(r.error);
    notify.success('予約を取り消しました');
    reload();
  };

  const columns: Column[] = useMemo(
    () => [
      {
        field: 'customer_name',
        headerName: '顧客',
        width: 220,
        renderCell: (row: MailDeliveryRow) => (
          <Typography
            component="span"
            onClick={() => navigate(`/crm/customers/${row.customer_id}`)}
            sx={{ fontSize: 13, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {row.customer_name || '(顧客名なし)'}
          </Typography>
        ),
      },
      { field: 'recipient_name', headerName: '宛名', width: 120 },
      { field: 'email', headerName: 'メール', width: 240 },
      { field: 'status', headerName: '状態', width: 90, renderCell: (row: MailDeliveryRow) => <DeliveryStatusChip status={row.status} /> },
      { field: 'sent_at', headerName: '送信', width: 130, renderCell: (r: MailDeliveryRow) => fmtDateTime(r.sent_at) },
      { field: 'delivered_at', headerName: '配信成功', width: 130, renderCell: (r: MailDeliveryRow) => fmtDateTime(r.delivered_at) },
      { field: 'opened_at', headerName: '開封', width: 130, renderCell: (r: MailDeliveryRow) => fmtDateTime(r.opened_at) },
      { field: 'click_count', headerName: 'クリック', width: 70, align: 'right' },
      {
        field: 'diagnostic',
        headerName: 'エラー内容',
        width: 320,
        renderCell: (r: MailDeliveryRow) => {
          const t = [r.bounce_type, r.bounce_sub_type].filter(Boolean).join(' / ');
          const d = r.diagnostic || r.error_message || '';
          if (!t && !d) return '';
          return (
            <Tooltip title={d}>
              <span style={{ fontSize: 12 }}>{[t, d].filter(Boolean).join(' ')}</span>
            </Tooltip>
          );
        },
      },
    ],
    [navigate],
  );

  if (loading && !campaign) return <LoadingView message="配信を読み込んでいます..." height="80vh" />;
  if (error || !campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || '配信が見つかりません'}</Alert>
        <Button sx={{ ...STANDARD_TEXT_BUTTON_SX, mt: 2 }} startIcon={<Icons.ArrowBack />} onClick={() => navigate('/mail-delivery')}>
          配信履歴へ戻る
        </Button>
      </Box>
    );
  }

  const c = campaign;
  const total = c.total_count || 0;
  const progress = total ? Math.min(100, Math.round(((c.sent_count || 0) / total) * 100)) : 0;
  const editable = c.status === 'draft' || c.status === 'scheduled';
  const cards: { label: string; value: string; color?: string }[] = [
    { label: '配信数', value: total.toLocaleString() },
    { label: '送信', value: fmtCountRate(c.sent_count, total) },
    { label: '配信成功', value: fmtCountRate(c.delivered_count, total), color: '#2e7d32' },
    { label: 'バウンス', value: fmtCountRate(c.bounce_count, total), color: c.bounce_count ? '#c62828' : undefined },
    { label: '苦情', value: fmtCountRate(c.complaint_count, total), color: c.complaint_count ? '#c62828' : undefined },
    { label: '失敗', value: fmtCountRate(c.failed_count, total), color: c.failed_count ? '#c62828' : undefined },
    { label: '除外', value: (c.suppressed_count || 0).toLocaleString() },
    { label: '開封', value: fmtCountRate(c.open_count, total) },
    { label: 'クリック', value: (c.click_count || 0).toLocaleString() },
  ];

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 2, width: '96%', margin: '0 auto' }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/mail-delivery')} sx={{ mt: 0.25 }}>
          <Icons.ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: blueGrey[700] }}>{c.subject}</Typography>
            <CampaignStatusChip status={c.status} />
            {c.legacy_no != null && <Chip size="small" variant="outlined" label={`ブラストメールから移行 (配信番号 ${c.legacy_no})`} sx={{ fontSize: 11, height: 22 }} />}
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No {c.id}</Typography>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
            送信元 {c.from_display_name || c.from_email || ''} ／ 配信リスト {c.list_names || '(未選択)'} ／
            {c.status === 'scheduled' ? ` 予約 ${fmtDateTime(c.scheduled_at)}` : ` 開始 ${fmtDateTime(c.started_at) || '-'}`}
            {c.finished_at ? ` ／ 完了 ${fmtDateTime(c.finished_at)}` : ''}
            {c.created_operator_id ? ` ／ 作成 ${c.created_operator_id}` : ''}
          </Typography>
          {c.error_message && (
            <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>
              {c.error_message}
            </Alert>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {editable && (
            <Button size="small" sx={STANDARD_TEXT_BUTTON_SX} startIcon={<Icons.Edit />} onClick={() => navigate(`/mail-delivery/campaigns/${c.id}/edit`)}>
              編集
            </Button>
          )}
          {c.status === 'scheduled' && (
            <Button size="small" sx={STANDARD_TEXT_BUTTON_SX} startIcon={<Icons.Cancel />} onClick={handleCancel}>
              予約取消
            </Button>
          )}
          <Button size="small" sx={STANDARD_CTA_BUTTON_SX} startIcon={<Icons.ContentCopy />} onClick={handleCopy}>
            複製して新規作成
          </Button>
        </Box>
      </Box>

      {/* 送信中の進捗 */}
      {c.status === 'sending' && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
            送信中 {(c.sent_count || 0).toLocaleString()} / {total.toLocaleString()} 通 ({progress}%)。10 秒ごとに更新します。
          </Typography>
        </Box>
      )}

      {/* サマリーカード */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 1, mb: 1.5 }}>
        {cards.map((k) => (
          <Paper key={k.label} variant="outlined" sx={{ p: 1, textAlign: 'center', borderColor: '#e2e8f0' }}>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{k.label}</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: k.color || blueGrey[800] }}>{k.value}</Typography>
          </Paper>
        ))}
      </Box>

      {/* タブ */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #e2e8f0', mb: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 13, py: 0.5 } }}>
          <Tab value="recipients" label={`宛先一覧 (${deliveries.total.toLocaleString()})`} />
          <Tab value="preview" label="本文プレビュー" />
        </Tabs>
        {tab === 'recipients' && (
          <>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(Object.keys(DELIVERY_STATUS_LABEL) as MailDeliveryStatus[])
                .filter((s) => deliveries.statusCounts[s])
                .map((s) => (
                  <Chip
                    key={s}
                    size="small"
                    label={`${DELIVERY_STATUS_LABEL[s]} ${deliveries.statusCounts[s].toLocaleString()}`}
                    variant={deliveries.status === s ? 'filled' : 'outlined'}
                    onClick={() => deliveries.changeStatus(deliveries.status === s ? '' : s)}
                    sx={{ fontSize: 11.5, height: 24 }}
                  />
                ))}
            </Box>
            <TextField
              select
              size="small"
              value={deliveries.status}
              onChange={(e) => deliveries.changeStatus(e.target.value as MailDeliveryStatus | '')}
              sx={{ width: 120, '& .MuiInputBase-input': { fontSize: 12.5, py: 0.6 } }}
            >
              <MenuItem value="">すべて</MenuItem>
              {(Object.keys(DELIVERY_STATUS_LABEL) as MailDeliveryStatus[]).map((s) => (
                <MenuItem key={s} value={s}>
                  {DELIVERY_STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="顧客名 / 宛名 / メール"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') deliveries.changeQuery(qInput.trim());
              }}
              onBlur={() => deliveries.changeQuery(qInput.trim())}
              sx={{ width: 220, '& .MuiInputBase-input': { fontSize: 12.5, py: 0.6 } }}
            />
            <IconButton size="small" onClick={deliveries.reload}>
              <Icons.Refresh fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      {tab === 'recipients' ? (
        <Box sx={TABLE_FILL_SX}>
          <StandardDataTable
            rows={deliveries.rows}
            columns={columns}
            loading={deliveries.loading}
            loadingMessage="宛先を取得中"
            totalCount={deliveries.total}
            page={deliveries.page}
            pageSize={deliveries.pageSize}
            onPageChange={deliveries.setPage}
            onPageSizeChange={deliveries.changePageSize}
            onSortChange={() => {}}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 300 }}>
          <MailPreviewFrame html={previewHtml} height="100%" emptyMessage="プレビューを生成しています..." />
        </Box>
      )}
    </Box>
  );
};

export default MailDeliveryCampaignDetail;
